import { createContext, memo, ReactNode, useCallback, useMemo, useState } from "react";

type SpreadsheetHighlightContextType = {
  highlightedColumns: { [key: string]: string },
  highlightedRows: { [key: number]: string },
  registerColumn: (id: string, column: string | null | undefined, color: string) => void
  registerRow: (id: string, row: number | null | undefined, color: string) => void
}

export const SpreadsheetHighlightContext = createContext<SpreadsheetHighlightContextType>({
  highlightedColumns: {},
  highlightedRows: {},
  registerColumn: console.log,
  registerRow: console.log,
});

export const SpreadsheetHighlightContextProvider = memo<{ children: ReactNode | ReactNode[] }>(({ children }) => {
  const [ columns, setColumns ] = useState<{ [key: string]: { column: string, color: string } }>({});
  const [ rows, setRows ] = useState<{ [key: string]: { row: number, color: string } }>({});

  const registerColumn = useCallback((id: string, column: string | null | undefined, color: string) => {
    setColumns(({ [id]: storedPair, ...prevState }) => {
      const newValue = {
        ...prevState,
      };

      if (column) {
        Object.assign(newValue, { [id]: { column, color } });
      }

      return newValue;
    });
  }, []);

  const highlightedColumns = useMemo(() => Object.values(columns)
    .reduce((acc, val) => ({ ...acc, [val.column]: val.color }), {}), [ columns ]);

  const registerRow = useCallback((id: string, row: number | null | undefined, color: string) => {
    setRows(({ [id]: storedPair, ...prevState }) => {
      const newValue = {
        ...prevState,
      };

      if (row) {
        Object.assign(newValue, { [id]: { row, color } });
      }

      return newValue;
    });
  }, []);

  const highlightedRows = useMemo(() => Object.values(rows)
    .reduce((acc, val) => ({ ...acc, [val.row]: val.color }), {}), [ rows ]);

  const providerValue: SpreadsheetHighlightContextType = useMemo(() => ({
    highlightedColumns,
    highlightedRows,
    registerColumn,
    registerRow,
  }), [ highlightedColumns, registerColumn, highlightedRows, registerRow ]);

  return (
    <SpreadsheetHighlightContext.Provider value={providerValue}>
      {children}
    </SpreadsheetHighlightContext.Provider>
  );
});
